export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function recordFrom(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeValidationMessage(message: string): string {
  return message.replace(/^(?:Value error|Assertion failed), /, "");
}

function validationMessagesFromDetail(
  detail: Record<string, unknown>,
): string[] {
  const errors = detail.errors;
  if (!Array.isArray(errors)) return [];

  return errors
    .map((entry) => {
      const item = recordFrom(entry);
      const message = item?.message;
      return typeof message === "string" && message.trim()
        ? normalizeValidationMessage(message.trim())
        : null;
    })
    .filter((message): message is string => message !== null);
}

/**
 * Parse the backend's standard envelope (`{ status_code, message, data }`)
 * into an ApiError. Every backend response — success, HTTPException,
 * validation error, unhandled exception — uses this shape.
 */
export function parseApiError(body: unknown, status: number): ApiError {
  const envelope = recordFrom(body);
  const data = recordFrom(envelope?.data);
  const detail = recordFrom(envelope?.detail);
  const errorDetail = data ?? detail ?? envelope ?? {};
  const validationMessages = validationMessagesFromDetail(errorDetail);

  const message =
    (validationMessages.length > 0 && validationMessages.join(" ")) ||
    (typeof envelope?.message === "string" && envelope.message) ||
    (typeof detail?.message === "string" && detail.message) ||
    "Request failed";
  const code =
    (typeof errorDetail.code === "string" && errorDetail.code) || "UNKNOWN";

  return new ApiError(message, code, status, errorDetail);
}

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

/** User-facing copy for toast descriptions and inline error text. */
export function getApiErrorMessage(
  error: unknown,
  fallback = DEFAULT_ERROR_MESSAGE,
): string {
  if (error instanceof ApiError && error.message.trim()) {
    return error.message;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}
